import { wccModules } from 'enums/wccModules';
import { DOMHelpers } from 'helpers/dom';
import { inject, injectable } from 'inversify';
import { Observable, PureComputed, Subscribable } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import localStorage from 'managers/localStorage';
import { NodeTracker } from 'managers/nodes/tracker';
import { TopicManager } from 'managers/topic';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import { Person } from 'models/person';
import { Topic } from 'models/topic';
import { JSONTopicThread, TopicThread } from 'models/topicThread';
import WCCError from 'models/wccError';
import { ServicesContext } from 'services/context';
import { IThreadEditorVM, ThreadEditorVMConfig } from './thread.interfaces';
import './thread.scss';
import {has} from "underscore";
import DiscussionTranslationManager from "../../../managers/discussion/translation";
import { ImageHelpers } from '../../../helpers/images';
import { URLHelpers } from '../../../helpers/url';

components
    .registerLocal('thread-editor-visibility-button', () => require('./visibilityButton'))
    .registerLocal('thread-editor-visibility-options', () => require('./visibilityOptions'))
    .registerLocal('thread-editor-participants-picker', () => require('./participantsPicker'))
    .registerLocal('thread-editor-visibility-tags-picker', () => require('./visibilityTagsPicker'))
    .registerLocal('thread-editor-picker', () => require('./picker'));

@injectable()
export default abstract class ThreadEditorVM<T = any> implements IThreadEditorVM {
    private threadStateAwaiter: Subscribable<Observable<T | undefined> | undefined>
    private threadState: Subscribable<T | undefined>

    private isSaveFailed = ko.observable(false);

    private shouldClearParentOnSave: Subscribable<boolean>

    private onSavedHandler?: Action<[Array<JSONTopicThread>]>
    private onCancelHandler?: Action

    labels = labels;

    threadVisibility = ko.observable<number>();
    threadParticipantIDs = ko.observableArray<string>();
    threadVisibilityTagIDs = ko.observableArray<string>();

    isSaving = ko.observable(false);

    user: Subscribable<Person | undefined>
    topic: Subscribable<Topic | undefined>
    topicId: Subscribable<string | undefined>

    threadOwner: Subscribable<Person | undefined>
    threadOwnerId: Subscribable<string | undefined>

    thread: Observable<TopicThread | undefined>
    parentThread: PureComputed<TopicThread | undefined>
    parentThreadId: Subscribable<string | undefined>

    allowToSave: Subscribable<boolean>
    allowToCancel: Subscribable<boolean>

    isReady: Subscribable<boolean>
    isLoading: Subscribable<boolean>
    isBusy: Subscribable<boolean>
    isReplyEditor: Subscribable<boolean>
    isEditMode: Subscribable<boolean>
    isPreviewMode: Subscribable<boolean>
    hasChanges: Subscribable<boolean>
    
    oTranslationNeeded: Subscribable<boolean>

    cHasModReply: PureComputed<boolean>;
    cCanHaveSuggestionsUI: PureComputed<boolean>;
    cReplyContent: PureComputed<Observable<string> | undefined>;
    
    oReplySuggestion = ko.observable<string | undefined>();
    oTranslatedReplySuggestion = ko.observable<string | undefined>();

    materialUIEnabled = ko.pureComputed(() => settings.isMaterialUIEnabled());

    constructor(
        @inject(wccModules.componentConfig) model: ThreadEditorVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) protected storage: IWCCStorageManager,
        @inject(wccModules.servicesContext) protected ctx: ServicesContext,
        @inject(wccModules.effects) protected effects: EffectsContainer
    ) {
        this.isPreviewMode = ko.flattenComputed(model.preview, false);

        const topicId = ko.flattenComputed(model.topicId);
        const thread = ko.flattenComputed(model.thread);
        
        this.thread = ko.observable(thread()).extend({ deferred: true, notifyIfChanged: true })

        const threadTopicId = this.thread.pluck(th => th.topicId);
        const threadParentId = this.thread.pluck(th => th.parentId);

        const parentThread = ko.flattenComputed(model.parentThread);

        this.parentThread = ko.observable(parentThread).unwrap();
        this.parentThreadId = this.parentThread.pluck(th => th.id, threadParentId);

        const parentThreadTopicId = this.parentThread.pluck(th => th.topicId);
        this.topicId = threadTopicId.default(parentThreadTopicId).default(topicId);

        this.shouldClearParentOnSave = ko.pureComputed(() => this.parentThread() != undefined && this.parentThread() != parentThread());

        this.onSavedHandler = model.onSave;
        this.onCancelHandler = model.onCancel;

        const userManager = storage.get(CurrentUserManager, { discussionId: settings.discussionId });
        this.user = userManager.pluck(m => m.person);

        const topicManager = this.storage.get(TopicManager, { topicId: this.topicId });
        this.topic = topicManager.pluck(m => m.topic);

        this.threadOwner = this.thread.mapNotNull(th => Person.fromThread(th)).default(this.user);
        this.threadOwnerId = this.threadOwner.pluck(o => o.personId);

        const { height } = new NodeTracker({ node: info.element as HTMLElement });
        const isRendered = ko.pureComputed(() => height() > 0);

        this.threadStateAwaiter = this.thread.mapNotNull(thread => system.getPromiseAwaiter(Promise.resolve(this.getStateFromThread(thread)), ex => {
            this.thread(undefined);
            system.handleError(ex);
        }), this, true);

        this.threadState = ko.pureComputed(() => ko.unwrap(this.threadStateAwaiter()));

        this.isReady = ko.pureComputed(() => this.checkIfReady() && (this.thread() == undefined || this.threadState() != undefined));
        this.isBusy = ko.pureComputed(() => this.checkIfBusy());
        this.hasChanges = ko.pureComputed(() => this.checkIfHasChanges());

        this.allowToSave = ko.pureComputed(() => this.isReady() && !this.isBusy() && this.checkIfAllowedToSave());
        this.allowToCancel = ko.pureComputed(() => this.isReady() && !this.isBusy() && (this.hasChanges() || this.checkIfAllowedToCancel()));

        this.isLoading = this.isReady.not();
        this.isReplyEditor = this.parentThreadId.isNotNull();
        this.isEditMode = this.thread.isNotNull();

        this.cHasModReply = ko.pureComputed(() => {
            let hasModReply = this.parentThread()?.replies()?.length == 0 ? false : false;
            if(this.parentThread()?.replies()?.length 
                && _.find(this.parentThread()?.replies() as TopicThread[], r => r.isModerator())) {
                hasModReply = true;
            }
            return hasModReply;
        });
        this.cCanHaveSuggestionsUI = ko.pureComputed(() => this.topic()?.cCanHaveSuggestionsUI() == true);
            //settings.summariseableActivityTypes.indexOf(this.topic()?.activityType() ?? -1) !== -1);
            //&& !this.cHasModReply());
        this.cReplyContent = ko.pureComputed(() => {
            return (this as any).text;
        });

        const translationManager = storage.get(DiscussionTranslationManager, { discussionId: settings.discussionId });
        this.oTranslationNeeded = translationManager.pluck('isAllowedToShow', false);

        //awaiting full construction
        requestAnimationFrame(() => {
            effects.register((thread, isRendered) => {
                if (thread != undefined && isRendered && !DOMHelpers.isInViewPort(info.element as HTMLElement))
                    DOMHelpers.scrollTo(info.element as HTMLElement);
            }, [this.thread, isRendered]);

            effects.register(thread => {
                if (thread != undefined)
                    this.thread(thread);
            }, [thread]);

            effects.register([
                () => {
                    this.thread()?.editing(false);
                }
            ]);

            effects.register(async (thread, threadState, parentThreadId, topicId, threadOwnerId) => {
                //Editor can flick isReady flag during it's life time
                //but we need to reinit it only when main params are changed(this func args)
                let initRequired = true;

                return withEffect(async isReady => {
                    if (isReady) {
                        const isInitCycle = initRequired;

                        initRequired = false;

                        if (isInitCycle)
                            this.reset(); //reset editor to base state

                        if (threadState == undefined) {
                            if (topicId != undefined && threadOwnerId != undefined) {
                                //Find localstorage unit for this editor
                                const scope = parentThreadId == undefined ?
                                    localStorage.newComment(topicId, threadOwnerId) :
                                    localStorage.newReply(topicId, threadOwnerId, parentThreadId);

                                if (isInitCycle) {
                                    //Restore previous state from localstorage
                                    var oldStateStr = scope.get();

                                    if (oldStateStr != undefined) {
                                        //validate state (expired s3 links need to be purged)
                                        let oldState = JSON.parse(oldStateStr);
                                        ////console.log('setState BEFORE validate', oldState);
                                        //await this.validateAndUpdateS3Links(oldState);
                                        //console.log('setState AFTER validate', oldState);
                                        this.setState(oldState);
                                    }
                                }

                                const state = ko.pureComputed(() => this.getState());

                                const saveState = (state: T | undefined) => {
                                    if (state != undefined)
                                        scope.set(JSON.stringify(state));
                                    else
                                        scope.remove();
                                }

                                //Start writing current state to localstorage
                                return withEffect(state => saveState(state), [state]);
                            }
                        } else if (thread != undefined) {
                            if (isInitCycle) {
                                //init editor from thread and provided state
                                this.setStateFromThread(thread);
                                this.setState(threadState);
                            }
                        }
                    }
                }, [this.isReady]);
            }, [this.thread, this.threadState, this.parentThreadId, this.topicId, this.threadOwnerId]);

            const shouldAutoSave = ko.pureComputed(() => this.checkIfShouldAutoSave());

            effects.register((allowToSave, shouldAutoSave, isSaveFailed) => {
                if (allowToSave && shouldAutoSave && !isSaveFailed)
                    this.save();
            }, [this.allowToSave, shouldAutoSave, this.isSaveFailed]);
        });

        ko.applyBindingsToNode(info.element, {
            'css': {
                'thread-editor': true
            }
        });
    }

    save = async () => {
        await this.saveAdvanced();
    }

    cancel = (force?: any) => {
        if (force === true) {
            this.cancelInternal();
        } else if (this.allowToCancel()) {
            if (this.hasChanges())
                system.confirm(messages.AreYouSureYouWantToCancelAllYourChanges, () => this.cancelInternal());
            else
                this.cancelInternal();
        }
    }

    async saveAdvanced(overrides?: JSONTopicThread) {
        if (this.allowToSave()) {
            const errorOrResult = this.validate();

            if (_.isString(errorOrResult)) {
                system.handleError(new WCCError(errorOrResult));
            } else if (errorOrResult === true || errorOrResult == undefined) {
                try {
                    this.isSaving(true);

                    const thread = this.thread();

                    const visibilityOptions: JSONTopicThread = {
                        Visibility: this.threadVisibility(),
                        ThreadParticipantIDs: this.threadParticipantIDs(),
                        ThreadVisibilityTagIDs: this.threadVisibilityTagIDs()
                    }

                    const saveDataRaw = this.getSaveData();
                    
                    if(!_.isArray(saveDataRaw) && WCC.Settings.NotNativeSpeaker === true) {
                        const threadJson = (saveDataRaw as JSONTopicThread);
                        threadJson.IsForcedContentTranslationNeeded = true;
                        if (this.isReplyEditor() && this.oTranslatedReplySuggestion()) {
                            // Thread is a reply, has been built from reply suggestion and thread creator is not a native speaker 
                            if (threadJson.ThreadContent === this.oTranslatedReplySuggestion()) {
                                // Suggestion has not been edited, and we can use suggestion that we have already got without translating it
                                threadJson.ThreadContent = this.oReplySuggestion();
                                threadJson.IsForcedContentTranslationNeeded = false;
                            }
                        }
                    }
                    
                    const saveDataList = _.isArray(saveDataRaw) ? saveDataRaw : [saveDataRaw];

                    if (saveDataList.length == 0)
                        throw new WCCError(messages.UnknownError);

                    const jsonThreads = saveDataList.map(saveData => {
                        if (thread != undefined)
                            return _({}).extend(thread.toJson(), visibilityOptions, saveData, overrides);
                        else
                            return _({}).extend(this.getBaseThreadJson(), visibilityOptions, saveData, overrides);
                    });

                    if (jsonThreads.length == 1) {
                        if (thread != undefined)
                            await this.updateThread(jsonThreads[0]);
                        else
                            await this.saveThread(jsonThreads[0]);
                    } else {
                        if (thread != undefined)
                            throw new WCCError(messages.UnknownError);
                        else
                            await this.saveMultipleThreads(jsonThreads);
                    }

                    this.isSaveFailed(false);

                    this.finishEditing();

                    if (this.onSavedHandler) this.onSavedHandler(jsonThreads);
                    
                    try {
                        if (WCC.Settings.ReloadPageAfterSubmitting == true) {
                            window.location.reload();
                        }
                    }catch (ex) {
                        console.log(ex);
                    }
                        
                } catch (ex) {
                    system.handleError(ex, true);
                    this.isSaveFailed(true);
                } finally {
                    this.isSaving(false);
                }
            }
        }
    }

    protected abstract getSaveData(): JSONTopicThread | Array<JSONTopicThread>
    protected abstract getState(): T | undefined
    protected abstract setState(data: T): void
    protected abstract getStateFromThread(thread: TopicThread): Promise<T> | T

    protected abstract validate(): string | undefined | boolean

    protected checkIfReady() {
        return this.user() != undefined &&
            this.threadOwner() != undefined &&
            this.topic() != undefined &&
            (this.threadStateAwaiter() == undefined || this.threadState() != undefined);
    }

    protected checkIfHasChanges() {
        return this.thread() != undefined;
    }

    protected checkIfAllowedToSave() {
        return true;
    }

    protected checkIfAllowedToCancel() {
        return false;
    }

    protected checkIfBusy() {
        return this.isSaving();
    }

    protected checkIfShouldAutoSave() {
        return false;
    }

    protected async saveThread(jsonThread: JSONTopicThread): Promise<void> {
        await this.ctx.commentsService.save(jsonThread);
    }

    protected async saveMultipleThreads(jsonThreads: Array<JSONTopicThread>): Promise<void> {
        await this.ctx.commentsService.saveMultiple(jsonThreads);
    }

    protected async updateThread(jsonThread: JSONTopicThread): Promise<void> {
        await this.ctx.commentsService.update(jsonThread);
    }

    protected cancelInternal() {
        this.finishEditing();
        
        this.oReplySuggestion(undefined);
        this.oTranslatedReplySuggestion(undefined);

        if (this.onCancelHandler)
            this.onCancelHandler();
    }

    protected reset() {
        this.threadVisibility(undefined);
        this.threadParticipantIDs([]);
        this.threadVisibilityTagIDs([]);
        this.isSaveFailed(false);
    }

    protected finishEditing() {
        this.finishThreadEditing();

        this.reset();
        this.saveState();

        if (this.shouldClearParentOnSave())
            this.parentThread(undefined);
    }

    private finishThreadEditing() {
        const thread = this.thread();

        if (thread != undefined) {
            thread.editing(false);
            this.thread(undefined);
        }
    }

    private getS3Attachments(attachments: []): any[] {
        let s3Attachments: any[] = [];

        attachments.forEach((attachment: any) => {
            let s3signedFiles = attachment.Files.filter((file: any) => file && URLHelpers.isS3SignedLink(file.Link));

            if (s3signedFiles.length > 0) {                
                s3Attachments.push(attachment);
            }
        });

        return s3Attachments;
    }

    //
    //  Validate and clean up any expired s3 signed links
    //
    private async validateAndUpdateS3Links(state: any) {
        if (state.Attachments) {
            console.log('state.Attachments BEFORE', state.Attachments);

            let s3Attachments = this.getS3Attachments(state.Attachments);

            let s3AttachmentPromises: Promise<any>[] = [];

            s3Attachments.forEach((attachment: any) => {
                attachment.Files.forEach((file: any) => {
                    s3AttachmentPromises.push(ImageHelpers.checkIfS3ImageHasExpired(file.UserFileId, file.Link));
                });
            });

            console.log('S3 validation promises', s3AttachmentPromises);

            let s3AttachmentResults = await Promise.all(s3AttachmentPromises);

            console.log('S3 validation promise results', s3AttachmentResults);

            // filter our current attachments array with only valid s3 attachments
            let newAttachments = state.Attachments.filter(
                (attachment: any) => attachment.Files.some(
                    (file: any) => s3AttachmentResults.some(
                        (result: any) => result.id == file.UserFileId && result.exists)));

            console.log('Valid S3 attachments', newAttachments);

            // notify users that expired images have been removed
            if (state.Attachments.length != newAttachments.length) {
                system.showInfoMessage('NOTE: Expired image/video uploads have been removed from local cache for security reasons.');
            }

            // update our state attachments with changes (if applicable)
            state.Attachments = newAttachments;
            
            console.log('state.Attachments AFTER', state.Attachments);
        }
    }


    private saveState() {
        if (this.thread() == undefined) {
            const topicId = this.topicId();
            const threadOwnerId = this.threadOwnerId();
            const parentThreadId = this.parentThreadId()

            if (topicId != undefined && threadOwnerId != undefined) {
                const scope = parentThreadId == undefined ?
                    localStorage.newComment(topicId, threadOwnerId) :
                    localStorage.newReply(topicId, threadOwnerId, parentThreadId);

                const state = this.getState();

                if (state != undefined)
                    scope.set(JSON.stringify(state));
                else
                    scope.remove();
            }
        }
    }

    private setStateFromThread(thread: TopicThread) {
        this.threadVisibility(thread.visibility());
        this.threadParticipantIDs(thread.threadParticipantIDs());
        this.threadVisibilityTagIDs(thread.threadVisibilityTagIDs());
    }

    private getBaseThreadJson(): JSONTopicThread {
        return {
            ParentThreadId: this.parentThreadId(),
            DiscussionTopicId: this.topicId(),
            PostedById: this.threadOwner()?.personId(),
            ThreadCreator: this.threadOwner()?.nicknameOrName(),
            ThreadCreatorUserImage: this.threadOwner()?.imageURL(),
            CreatorTaskMemberStatus: this.threadOwner()?.status()
        }
    }
}